/**
 * User Manager — user page (ORCH-336, ORCH-21 §12; PRD User Manager UM-4 to UM-9, UM-12, UM-15)
 *
 * Purpose: One person in the tenant being managed. The Details tab shows their
 *          shared Profile (with their Company in this tenant, ORCH-410 §4.2) and,
 *          below it, their Access Rights in this tenant: roles and security
 *          attributes. History lists every
 *          change to that access. A system admin in GLOBAL also gets All
 *          Tenants: the account itself and every tenant the person belongs to.
 * Logic: Changes are drafted, counted ("N Unsaved Changes"), reviewed as a
 *        list and only then saved by the page's one Save: the profile, then the
 *        attributes, then roles, so a role that inherits the attributes is
 *        judged against the new ones.
 *        The server decides what the caller may do; `_authz` on the membership
 *        and `assignable` on each role only shape what is offered, through the
 *        status action matrix (`memberActions`, ORCH-420). An invitation opens
 *        like any member: the Invited chip, when it was sent and expires, Resend
 *        and Cancel Invite in the header, and an Invitee card with only the
 *        names, Email and Company, since the person fills the rest in themselves.
 *        Drafted changes are also guarded: anything that would navigate away
 *        from the page asks before the edits are lost (ORCH-393).
 * Dependencies: ProfileCard, MembershipForm, ReviewChangesDialog,
 *               MembershipHistory, ConfirmDialog, NavigationGuardContext,
 *               memberActions, userManagementService
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import {
  Home as HomeIcon,
  Settings as SettingsIcon,
  Groups as UsersIcon,
} from '@mui/icons-material';
import SkeletonPage from '../../../components/layout/SkeletonPage';
import {
  ConfirmDialog,
  DestructiveButton,
  LoadingButton,
  NotificationSnackbar,
  StatusChip,
} from '../../../components/common';
import AddToTenantDialog from '../../../components/user/AddToTenantDialog';
import CompanyField from '../../../components/user/CompanyField';
import MembershipForm from '../../../components/user/MembershipForm';
import MembershipHistory from '../../../components/user/MembershipHistory';
import ProfileCard, { ProfileCardHandle } from '../../../components/user/ProfileCard';
import ReviewChangesDialog from '../../../components/user/ReviewChangesDialog';
import {
  changedMetadata,
  cleanMetadata,
  COMPANY_FIELD,
  diffMembership,
  firstLast,
  givenName,
  inviteSummary,
  MembershipChange,
  MembershipDraft,
  membershipStatusLabel,
  profileChanges as describeProfileChanges,
  ProfileFieldChange,
  sameIds,
} from '../../../components/user/membership';
import { memberActions } from '../../../components/user/memberActions';
import { useMembershipFields } from '../../../components/user/useMembershipFields';
import { USER_MODEL_ID } from '../../../components/user/useProfileDerivations';
import { useAuth } from '../../../context/AuthContext';
import {
  useNavigationGuard,
  useUnsavedChangesGuard,
} from '../../../context/NavigationGuardContext';
import { PermissionKey } from '../../../constants/permissionKeys';
import { formatAuditTimestamp } from '../../../utils/formatting';
import userManagementService, {
  newChangeId,
  userErrorMessage,
} from '../../../services/userManagementService';
import { authzService } from '../../../services/authzService';
import { tenantService } from '../../../services/tenantService';
import { Role } from '../../../types/authz';
import { AllMemberships, Member, UserDetail } from '../../../types/userManagement';
import { EFFECTIVE_ACCESS_TAB_PARAM } from '../RoleManager/RoleManagerPage';
import { inviteEmailNote } from './UserManagementPage';

type TabKey = 'details' | 'history' | 'tenants';

const draftOf = (member: Member): MembershipDraft => ({
  roleIds: member.roles.map((r) => r.role_id),
  metadata: cleanMetadata(member.metadata || {}),
});

const initials = (member: Member) =>
  [member.first_name, member.last_name]
    .map((part) => (part || '').trim().charAt(0))
    .join('')
    .toUpperCase() || member.email.charAt(0).toUpperCase();

const UserPage: React.FC = () => {
  const { userId = '' } = useParams<{ userId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { state, can, refreshUser } = useAuth();
  const { confirmNavigation } = useNavigationGuard();

  const activeTenantId = state.user?.activeTenant?.tenant_id || '';
  const isSystemAdmin = !!state.user?.is_system_admin;
  // A system admin in GLOBAL names the tenant; everyone else works in their own
  const tenantId = (isSystemAdmin && searchParams.get('tenant')) || activeTenantId;
  const otherTenant = tenantId !== activeTenantId ? tenantId : undefined;

  const canSeeHistory = can(PermissionKey.AUDIT_VIEW);

  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('details');

  const [initial, setInitial] = useState<MembershipDraft>({ roleIds: [], metadata: {} });
  const [draft, setDraft] = useState<MembershipDraft>({ roleIds: [], metadata: {} });
  const [reviewOpen, setReviewOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState<
    'deactivate' | 'remove' | 'account' | 'reset' | 'cancelInvite' | null
  >(null);
  const [working, setWorking] = useState(false);
  const [allTenants, setAllTenants] = useState<AllMemberships | null>(null);
  const [tenantList, setTenantList] = useState<{ tenant_id: string; name: string }[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const profileRef = useRef<ProfileCardHandle>(null);
  // Each profile field whose value differs from the saved one (ORCH-410)
  const [profileFieldChanges, setProfileFieldChanges] = useState<ProfileFieldChange[]>([]);
  const profileDirty = profileFieldChanges.length > 0;
  // One id per Save, sent with each request it makes, so History shows one entry (ORCH-422)
  const changeIdRef = useRef<string | undefined>(undefined);

  const { attributeFieldIds, fieldLabel, valueName } = useMembershipFields(tenantId);
  // The profile form's labels and value names, for the review step's and History's profile entries
  const profileFields = useMembershipFields(tenantId, USER_MODEL_ID);
  const profileNames = useMemo(
    () => ({ fieldLabel: profileFields.fieldLabel, valueName: profileFields.valueName }),
    [profileFields.fieldLabel, profileFields.valueName]
  );

  const adopt = useCallback((member: Member) => {
    const next = draftOf(member);
    setInitial(next);
    setDraft(next);
  }, []);

  const load = useCallback(async () => {
    if (!userId || !tenantId) return;
    setLoading(true);
    try {
      const data = await userManagementService.getUser(userId, otherTenant);
      setDetail(data);
      adopt(data.membership);
      setLoadError(null);
    } catch (e) {
      setLoadError(userErrorMessage(e, 'This person could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [userId, tenantId, otherTenant, adopt]);

  useEffect(() => {
    load();
  }, [load]);

  // Which roles the caller may give this person (some depend on their attributes),
  // from the tenant being managed: its own roles as well as the standard ones
  useEffect(() => {
    if (!userId) return;
    authzService
      .listRoles(userId, otherTenant)
      .then(setRoles)
      .catch(() => setRoles([]));
  }, [userId, activeTenantId, otherTenant]);

  const loadAllTenants = useCallback(() => {
    userManagementService
      .getMemberships(userId)
      .then(setAllTenants)
      .catch((e) => setError(userErrorMessage(e, 'The tenants could not be loaded.')));
  }, [userId]);

  useEffect(() => {
    if (tab !== 'tenants' || !isSystemAdmin) return;
    loadAllTenants();
    tenantService
      .getAllTenants()
      .then((tenants) =>
        setTenantList(tenants.map((t) => ({ tenant_id: t.tenant_id, name: t.name })))
      )
      .catch(() => setTenantList([]));
  }, [tab, isSystemAdmin, loadAllTenants, detail]);

  const member = detail?.membership ?? null;
  // What this membership's status allows the caller to do (ORCH-420 §3.2)
  const allowed = useMemo(
    () => (member ? memberActions(member, can) : new Set<string>()),
    [member, can]
  );
  const isInvite = member?.status === 'invited';
  const mayEditAccess = allowed.has('edit');
  const mayAssign = allowed.has('assignRoles') && userId !== state.user?.userId;
  // A reset link is only useful to an active person here, and nobody sends one to themselves (ORCH-296)
  const maySendReset =
    allowed.has('passwordReset') &&
    detail?.profile.status === 'active' &&
    userId !== state.user?.userId;

  const roleName = useCallback(
    (roleId: string) =>
      roles.find((r) => r.role_id === roleId)?.name ||
      member?.roles.find((r) => r.role_id === roleId)?.name ||
      roleId,
    [roles, member]
  );

  // What the review step lists: each changed profile field by the form's own label, with
  // its value before and after, then the access changes
  const changes = useMemo<MembershipChange[]>(
    () => [
      ...describeProfileChanges(profileFieldChanges, profileNames),
      ...diffMembership(initial, draft, { roleName, fieldLabel, valueName }),
    ],
    [profileFieldChanges, profileNames, initial, draft, roleName, fieldLabel, valueName]
  );

  // The roles that read the person's attributes, named under the heading
  const attributeUsers = useMemo(
    () =>
      roles
        .filter(
          (r) =>
            draft.roleIds.includes(r.role_id) &&
            r.permissions.some((p) => Object.values(p.scopes).some((s) => s.mode === 'inherit'))
        )
        .map((r) => r.name)
        .sort(),
    [roles, draft.roleIds]
  );

  // Back to the stored membership: a new object resets the forms underneath
  const initialRef = useRef(initial);
  initialRef.current = initial;
  const discardDraft = useCallback(() => {
    const stored = { ...initialRef.current };
    setInitial(stored);
    setDraft(stored);
    profileRef.current?.reset();
  }, []);

  useUnsavedChangesGuard({
    when: changes.length > 0,
    message: `The ${changes.length} unsaved change${changes.length > 1 ? 's' : ''} to ${
      member ? firstLast(member) : 'this person'
    } will be lost.`,
    onDiscard: discardDraft,
  });

  const handleSave = async () => {
    if (!member) return;
    setSaving(true);
    changeIdRef.current = newChangeId();
    try {
      // The profile first: an invalid field stops the save before anything is written
      if (profileDirty && !(await profileRef.current?.save())) {
        setReviewOpen(false);
        return;
      }
      // Country, Locale or Clock may have changed what the signed-in person formats in
      if (profileDirty && userId === state.user?.userId) await refreshUser();
      let saved = member;
      const metadata = changedMetadata(initial.metadata, draft.metadata);
      if (Object.keys(metadata).length) {
        saved = await userManagementService.updateMembership(userId, tenantId, metadata, {
          changeId: changeIdRef.current,
        });
      }
      if (!sameIds(initial.roleIds, draft.roleIds)) {
        saved = await userManagementService.setRoles(
          userId,
          tenantId,
          draft.roleIds,
          changeIdRef.current
        );
      }
      setDetail((current) => (current ? { ...current, membership: saved } : current));
      adopt(saved);
      setReviewOpen(false);
      setNotice(`Saved. ${givenName(saved)}'s details have been updated.`);
    } catch (e) {
      setReviewOpen(false);
      setError(userErrorMessage(e, 'The changes could not be saved.'));
      // Part of the change may have gone through: show what is stored now
      load();
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    if (!member) return;
    setWorking(true);
    try {
      if (confirm === 'reset') {
        const sent = await userManagementService.sendPasswordReset(userId, otherTenant);
        setNotice(`Password reset email sent to ${sent.sent_to}`);
        setConfirm(null);
        return;
      }
      if (confirm === 'remove') {
        await userManagementService.removeMember(userId, tenantId);
        navigate('/admin/user-management');
        return;
      }
      if (confirm === 'cancelInvite') {
        await userManagementService.cancelInvitation(userId, tenantId);
        navigate('/admin/user-management');
        return;
      }
      if (confirm === 'account') {
        setAllTenants(
          await userManagementService.setAccountActive(
            userId,
            allTenants?.account_status !== 'active'
          )
        );
      } else {
        const saved = await userManagementService.setActive(userId, tenantId, false);
        setDetail((current) => (current ? { ...current, membership: saved } : current));
        setNotice(`${givenName(saved)} deactivated.`);
      }
      setConfirm(null);
    } catch (e) {
      setConfirm(null);
      setError(userErrorMessage(e, 'The change could not be made.'));
    } finally {
      setWorking(false);
    }
  };

  const reactivate = async () => {
    try {
      const saved = await userManagementService.setActive(userId, tenantId, true);
      setDetail((current) => (current ? { ...current, membership: saved } : current));
      setNotice(`${givenName(saved)} reactivated.`);
    } catch (e) {
      setError(userErrorMessage(e, 'Could not reactivate this person.'));
    }
  };

  // Resend needs no confirmation: it is harmless and undone by Cancel (ORCH-420 §2.2)
  const resendInvite = async () => {
    try {
      const sent = await userManagementService.resendInvitation(userId, tenantId);
      setDetail((current) => (current ? { ...current, membership: sent } : current));
      if (sent.email_status === 'sent') setNotice(`Invitation sent to ${sent.email}.`);
      else setError(`The email to ${sent.email} was not sent: ${inviteEmailNote(sent)}`);
    } catch (e) {
      setError(userErrorMessage(e, 'The invitation could not be resent.'));
    }
  };

  const title = member ? firstLast(member) : 'User';
  const tenantName =
    (otherTenant ? tenantId : state.user?.activeTenant?.name) || tenantId || 'this tenant';
  const breadcrumbs = [
    { label: 'Home', href: '/home', icon: <HomeIcon fontSize="inherit" /> },
    { label: 'Admin', href: '/admin', icon: <SettingsIcon fontSize="inherit" /> },
    {
      label: 'User Manager',
      href: '/admin/user-management',
      icon: <UsersIcon fontSize="inherit" />,
    },
    { label: title },
  ];

  if (loading && !detail) {
    return (
      <SkeletonPage pageTitle="User" breadcrumbs={breadcrumbs}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </SkeletonPage>
    );
  }

  if (loadError || !detail || !member) {
    return (
      <SkeletonPage pageTitle="User" breadcrumbs={breadcrumbs}>
        <Alert severity="error" data-testid="user-page-error">
          {loadError || 'This person could not be loaded.'}
        </Alert>
      </SkeletonPage>
    );
  }

  const profile = detail.profile;
  const ownProfile = userId === state.user?.userId;
  const profileHint = isInvite
    ? `The name the invitation was sent with. ${givenName(member)} fills in the rest once they accept.`
    : profile.can_edit_profile
      ? ownProfile
        ? 'Your profile, shared by every tenant you belong to.'
        : `Shared by every tenant ${givenName(member)} belongs to.`
      : `Shared across Orchestrate. You can read it; ${givenName(member)} edits it.`;

  return (
    <SkeletonPage pageTitle={title} breadcrumbs={breadcrumbs}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
        <Avatar sx={{ width: 36, height: 36, fontSize: 14 }}>{initials(member)}</Avatar>
        <Box>
          <Typography variant="h6" data-testid="user-page-name">
            {title}
          </Typography>
          {member.invite ? (
            <Typography variant="caption" color="text.secondary" data-testid="user-page-invited">
              {inviteSummary(member.invite, formatAuditTimestamp)}
              {member.email_status && member.email_status !== 'sent'
                ? ` · Email not sent: ${inviteEmailNote(member)}`
                : ''}
            </Typography>
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid="user-page-last-sign-in"
            >
              Last Sign-In{' '}
              {member.last_login_at ? formatAuditTimestamp(member.last_login_at) : 'Never'}
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: 1 }} />
        <StatusChip status={member.status} label={membershipStatusLabel(member.status)} />
        {allowed.has('resendInvite') && (
          <Button size="small" onClick={resendInvite} data-testid="user-page-resend-invite">
            Resend Invite
          </Button>
        )}
        {allowed.has('cancelInvite') && (
          <DestructiveButton
            size="small"
            onClick={() => setConfirm('cancelInvite')}
            data-testid="user-page-cancel-invite"
          >
            Cancel Invite
          </DestructiveButton>
        )}
        {allowed.has('effectiveAccess') && (
          <Button
            size="small"
            onClick={() =>
              confirmNavigation(() =>
                navigate(`/admin/role-manager?tab=${EFFECTIVE_ACCESS_TAB_PARAM}`, {
                  state: {
                    member: {
                      user_id: member.user_id,
                      email: member.email,
                      first_name: member.first_name ?? null,
                      last_name: member.last_name ?? null,
                      ...(otherTenant ? { tenant_id: otherTenant } : {}),
                    },
                  },
                })
              )
            }
            data-testid="user-page-effective-access"
          >
            Effective Access
          </Button>
        )}
        {maySendReset && (
          <Button
            size="small"
            onClick={() => setConfirm('reset')}
            data-testid="user-page-send-password-reset"
          >
            Send Password Reset Email
          </Button>
        )}
        {(allowed.has('deactivate') || allowed.has('reactivate')) && (
          <>
            {allowed.has('reactivate') ? (
              <Button size="small" onClick={reactivate} data-testid="user-page-reactivate">
                Reactivate
              </Button>
            ) : (
              <DestructiveButton
                size="small"
                onClick={() => setConfirm('deactivate')}
                data-testid="user-page-deactivate"
              >
                Deactivate
              </DestructiveButton>
            )}
            <DestructiveButton
              size="small"
              onClick={() => setConfirm('remove')}
              data-testid="user-page-remove"
            >
              Remove User
            </DestructiveButton>
          </>
        )}
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tab} onChange={(_, value: TabKey) => setTab(value)} aria-label="user tabs">
          <Tab value="details" label="Details" data-testid="user-tab-details" />
          {canSeeHistory && <Tab value="history" label="History" data-testid="user-tab-history" />}
          {isSystemAdmin && (
            <Tab value="tenants" label="All Tenants" data-testid="user-tab-tenants" />
          )}
        </Tabs>
      </Box>

      {tab === 'details' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <ProfileCard
            ref={profileRef}
            profile={profile}
            tenantId={activeTenantId}
            readOnly={!profile.can_edit_profile}
            hint={profileHint}
            variant={isInvite ? 'invite' : 'profile'}
            hideSave
            onChange={setProfileFieldChanges}
            // The Company in this tenant: shown with the profile, saved to the membership
            company={
              <CompanyField
                tenantId={tenantId}
                attributeFieldIds={attributeFieldIds}
                initialValue={initial.metadata[COMPANY_FIELD]}
                value={draft.metadata[COMPANY_FIELD]}
                onChange={(company) =>
                  setDraft((current) => ({
                    ...current,
                    metadata: { ...current.metadata, [COMPANY_FIELD]: company },
                  }))
                }
                permission={PermissionKey.USER_EDIT}
                readOnly={!mayEditAccess}
                locked={!!member.company_locked}
              />
            }
            onSave={async (metadata) => {
              const saved = await userManagementService.updateProfile(
                userId,
                { metadata },
                changeIdRef.current
              );
              setDetail((current) =>
                current
                  ? {
                      profile: { ...saved, can_edit_profile: current.profile.can_edit_profile },
                      membership: {
                        ...current.membership,
                        first_name: saved.first_name,
                        last_name: saved.last_name,
                      },
                    }
                  : current
              );
            }}
          />

          <Paper
            variant="outlined"
            sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
            data-testid="access-rights-card"
          >
            <Typography variant="h6">Access Rights</Typography>
            <MembershipForm
              tenantId={tenantId}
              roles={roles}
              heldRoles={member.roles}
              initial={initial}
              value={draft}
              onChange={setDraft}
              permission={PermissionKey.USER_EDIT}
              canAssignRoles={mayAssign}
              attributeFieldIds={attributeFieldIds}
              readOnly={!mayEditAccess && !mayAssign}
              canEditAttributes={mayEditAccess}
              attributeUsers={attributeUsers}
              showCompany={false}
            />
          </Paper>

          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
            data-testid="user-page-save-bar"
          >
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ mr: 'auto' }}
              data-testid="unsaved-changes-count"
            >
              {changes.length > 0 &&
                `${changes.length} Unsaved Change${changes.length > 1 ? 's' : ''}`}
            </Typography>
            <Button
              disabled={changes.length === 0 || saving}
              onClick={discardDraft}
              data-testid="access-discard"
            >
              Discard
            </Button>
            <LoadingButton
              variant="contained"
              disabled={changes.length === 0}
              onClick={() => setReviewOpen(true)}
              data-testid="access-save"
            >
              Save
            </LoadingButton>
          </Box>
        </Box>
      )}

      {tab === 'history' && (
        <MembershipHistory
          userId={userId}
          tenantId={otherTenant}
          fieldLabel={fieldLabel}
          valueName={valueName}
          profileNames={profileNames}
        />
      )}

      {tab === 'tenants' && isSystemAdmin && (
        <Paper
          variant="outlined"
          sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
          data-testid="all-tenants-panel"
        >
          <Box>
            <Typography variant="h6">All Tenants</Typography>
            <Typography variant="body2" color="text.secondary">
              The account and every tenant {givenName(member)} belongs to. Tenant admins never see
              this.
            </Typography>
          </Box>
          {!allTenants ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">
                  Account
                </Typography>
                <StatusChip
                  status={allTenants.account_status || 'active'}
                  label={membershipStatusLabel(allTenants.account_status || 'active')}
                />
                <Typography variant="body2" color="text.secondary">
                  Last sign-in anywhere:{' '}
                  {allTenants.last_login ? formatAuditTimestamp(allTenants.last_login) : 'Never'}
                </Typography>
                <Button
                  size="small"
                  color={allTenants.account_status === 'active' ? 'error' : 'primary'}
                  onClick={() =>
                    allTenants.account_status === 'active'
                      ? setConfirm('account')
                      : handleConfirmAccount()
                  }
                  data-testid="account-status-button"
                >
                  {allTenants.account_status === 'active'
                    ? 'Deactivate Account'
                    : 'Reactivate Account'}
                </Button>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tenant</TableCell>
                    <TableCell>Roles</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allTenants.memberships.map((m) => (
                    <TableRow key={m.tenant_id} data-testid={`tenant-row-${m.tenant_id}`}>
                      <TableCell>{m.tenant_name || m.tenant_id}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {m.roles.map((r) => (
                            <Chip key={r.role_id} size="small" label={r.name} />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <StatusChip status={m.status} label={membershipStatusLabel(m.status)} />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          onClick={() =>
                            // The draft belongs to the tenant being left behind
                            confirmNavigation(() => {
                              setSearchParams(
                                m.tenant_id === activeTenantId ? {} : { tenant: m.tenant_id }
                              );
                              setTab('details');
                            })
                          }
                          data-testid={`tenant-open-${m.tenant_id}`}
                        >
                          Open
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setAddOpen(true)}
                  data-testid="add-to-tenant-button"
                >
                  + Add to a Tenant
                </Button>
              </Box>
            </>
          )}
        </Paper>
      )}

      {isSystemAdmin && (
        <AddToTenantDialog
          open={addOpen}
          userId={userId}
          personName={title}
          tenants={tenantList.filter(
            (t) => !(allTenants?.memberships || []).some((m) => m.tenant_id === t.tenant_id)
          )}
          attributeFieldIds={attributeFieldIds}
          onClose={() => setAddOpen(false)}
          onAdded={(addedTenantId) => {
            setAddOpen(false);
            setNotice(`${givenName(member)} added to ${addedTenantId}.`);
            loadAllTenants();
          }}
        />
      )}

      <ReviewChangesDialog
        open={reviewOpen}
        personName={firstLast(member)}
        changes={changes}
        saving={saving}
        onSave={handleSave}
        onClose={() => setReviewOpen(false)}
      />

      <ConfirmDialog
        open={confirm === 'deactivate'}
        title={`Deactivate ${title}?`}
        confirmLabel="Deactivate"
        loading={working}
        onConfirm={handleConfirm}
        onClose={() => setConfirm(null)}
        dataTestId="deactivate-dialog"
      >
        {/* Wording from QA (ORCH-382, 2026-10-21). Remove User is the button beside Deactivate,
            shown under the same right, so the dialog can always name it. */}
        <Typography variant="body2">
          {title} will no longer be able to access {tenantName}. Their profile, roles, and security
          attributes will be kept, and you can reactivate them at any time. To remove them from{' '}
          {tenantName} instead, use Remove User.
        </Typography>
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm === 'reset'}
        title="Send Password Reset Email?"
        confirmLabel="Send Email"
        loading={working}
        onConfirm={handleConfirm}
        onClose={() => setConfirm(null)}
        dataTestId="password-reset-dialog"
      >
        <Typography variant="body2">
          Send a password reset email to {member.email}? They&apos;ll get a link to choose a new
          password. It works once and expires in 24 hours. Their current password keeps working
          until they use it.
        </Typography>
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm === 'cancelInvite'}
        title={`Cancel the invitation to ${title}?`}
        confirmLabel="Cancel Invite"
        confirmColor="error"
        loading={working}
        onConfirm={handleConfirm}
        onClose={() => setConfirm(null)}
        dataTestId="cancel-invite-dialog"
      >
        <Typography variant="body2">
          The link in their email will stop working. You can invite them again later.
        </Typography>
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm === 'remove'}
        title={`Remove ${title}?`}
        confirmLabel="Remove"
        confirmColor="error"
        loading={working}
        onConfirm={handleConfirm}
        onClose={() => setConfirm(null)}
        dataTestId="remove-dialog"
      >
        <Typography variant="body2">
          {givenName(member)} loses all access here. Their history is kept. To pause access instead,
          use Deactivate.
        </Typography>
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm === 'account'}
        title={`Deactivate ${title}'s Account?`}
        confirmLabel="Deactivate Account"
        confirmColor="error"
        loading={working}
        onConfirm={handleConfirm}
        onClose={() => setConfirm(null)}
        dataTestId="account-dialog"
      >
        <Typography variant="body2">
          {givenName(member)} is signed out of every tenant and can't sign in until the account is
          reactivated. Their access in each tenant is kept as it is.
        </Typography>
      </ConfirmDialog>

      <NotificationSnackbar
        open={!!error}
        onClose={() => setError(null)}
        message={error || ''}
        severity="error"
        autoHideDuration={8000}
      />
      <NotificationSnackbar
        open={!!notice}
        onClose={() => setNotice(null)}
        message={notice || ''}
        severity="success"
        autoHideDuration={5000}
      />
    </SkeletonPage>
  );

  async function handleConfirmAccount() {
    try {
      setAllTenants(await userManagementService.setAccountActive(userId, true));
    } catch (e) {
      setError(userErrorMessage(e, 'The account could not be reactivated.'));
    }
  }
};

export default UserPage;
